import React from 'react';
import { connect, useDispatch } from 'react-redux';
import { SEPARATOR, DESKTOP } from '@/constants';
import {
  getSelectedShield,
  getSelectedSection,
  getMatrix,
  getIsCopyingAvailable,
  getIsPastingAvailable,
  findSeparator,
  getMaterialWithLaserParameters
} from '@/helpers';
import { getBuildedMatfileTemplate } from '@/helpers/matfile';
import { getConfig } from '@/config/runtimeConfig';
import {
  handleZoomSliderChange,
  undoStepThunk,
  redoStepThunk,
  copySectionContent,
  pasteSectionContent,
  addPage,
  setLaserOutputQualityThunk
} from '@/actions';
import { useCopyPaste, useDelete } from '@/hooks';
import Header from './';

const HeaderContainer = props => {
  const {
    isUndoAvailable,
    isRedoAvailable,
    isCopyingAvailable,
    isPastingAvailable,
    isAddPageAvailable,
    isDeleteAvailable,
    isInfoAvailable,
    isBackButtonShown,
    isMaterialWithLaserParameters,
    materialWithLaserParameters,
    handleZoomSliderChange,
    copySectionContent,
    pasteSectionContent,
    addPage,
  } = props;

  const [isDotsMenuVisible, setIsDotsMenuVisible] = React.useState(false);
  const [isDeleteMenuVisible, setIsDeleteMenuVisible] = React.useState(false);
  const [isMatInfoVisible, setIsMatInfoVisible] = React.useState(false);
  const [isArticleNumberModalOpen, setArticleNumberModalOpen] = React.useState(false);
  const [isSaveAsModalOpen, setSaveAsModalOpen] = React.useState(false);
  const [isLanguageModalOpen, setLanguageModalOpen] = React.useState(false);
  const [filteredLanguage, setFilteredLanguage] = React.useState(null);
  const [isLaserOutputContextMenuOpen, setLaserOutputContextMenuOpen] = React.useState(false);
  const [isPrinterModalOpen, setPrinterModalOpen] = React.useState(false);
  const [isInputTypeFileSelected, setInputTypeFileSelected] = React.useState(false);
  const [isFontSizeCoefModalOpen, setFontSizeCoefModalOpen] = React.useState(false);
  const [isLaserOutputQualityModalOpen, setLaserOutputQualityModalOpen] = React.useState(false);
  const [isLoadCFWByArticleNumberModalOpen, setLoadCFWByArticleNumberModalOpen] = React.useState(false);
  const dispatch = useDispatch();

  useCopyPaste({ isCopyingAvailable, isPastingAvailable });
  useDelete({ isDeleteAvailable });
  
  const handleSliderChange = value => {
    handleZoomSliderChange(value);
  };

  const handleUndoClick = () => {
    if (isUndoAvailable) {
      dispatch(undoStepThunk());
    }
  };

  const handleRedoClick = () => {
    if (isRedoAvailable) {
      dispatch(redoStepThunk());
    }
  };

  const handleCopyClick = () => {
    if (isCopyingAvailable) {
      copySectionContent();
    }
  };

  const handlePasteClick = () => {
    if (isPastingAvailable) {
      pasteSectionContent({ withoutFormatting: false });
    }
  };

  const handleAddPageClick = () => {
    if (isAddPageAvailable) {
      addPage();
    }
  };

  const handleOpenDeleteMenuClick = () => {
    if (isDeleteAvailable) {
      setIsDeleteMenuVisible(true);
    }
  };

  const handleCloseDeleteMenu = () => {
    setIsDeleteMenuVisible(false);
  };

  const handleOpenInfoMenuClick = () => {
    if (isInfoAvailable) {
      setIsMatInfoVisible(true);
    }
  };

  const handleCloseInfoMenu = () => {
    setIsMatInfoVisible(false);
  };

  const handleOpenDotsMenuClick = () => {
    setIsDotsMenuVisible(true);
  };

  const handleCloseDotsMenu = () => {
    setIsDotsMenuVisible(false);
  };

  const handleArticleNumberModalOpen = value => {
    setArticleNumberModalOpen(value);
  };

  const handleSaveAsModalOpen = value => {
    setSaveAsModalOpen(value);
  };

  const handleLanguageModalOpen = value => {
    setLanguageModalOpen(value);
  };

  const handleSetFilteredLanguage = value => {
    setFilteredLanguage(value);
  };

  const handleLaserOutputClick = () => {
    if (materialWithLaserParameters.$2) {
      handleLaserOutputQualityModalOpen(true);
    } else {
      setLaserOutputContextMenuOpen(true);
    }
  }

  const handleLaserOutputContextMenuClose = () => {
    setLaserOutputContextMenuOpen(false);
  }

  const handleSetLaserOutputQuality = (value) => {
    dispatch(setLaserOutputQualityThunk(value));
  }

  const handlePrinterModalOpen = value => {
    setPrinterModalOpen(value);
  };

  const handleInputTypeFileSelection = value => {
    setInputTypeFileSelected(value);
  };

  const handleFontSizeCoefModalOpen = value => {
    setFontSizeCoefModalOpen(value);
  };

  const handleLaserOutputQualityModalOpen = (value) => {
    setLaserOutputQualityModalOpen(value);
  }

  const handleLoadCFWByArticleNumberModalOpen = value => {
    setLoadCFWByArticleNumberModalOpen(value);
  };

  return (
    <Header
      {...props}
      filteredLanguage={filteredLanguage}
      isDeleteMenuVisible={isDeleteMenuVisible}
      isMatInfoVisible={isMatInfoVisible}
      isDotsMenuVisible={isDotsMenuVisible}
      isBackButtonShown={isBackButtonShown}
      isArticleNumberModalOpen={isArticleNumberModalOpen}
      isSaveAsModalOpen={isSaveAsModalOpen}
      isLanguageModalOpen={isLanguageModalOpen}
      isLaserOutputContextMenuOpen={isLaserOutputContextMenuOpen}
      isMaterialWithLaserParameters={isMaterialWithLaserParameters}
      isPrinterModalOpen={isPrinterModalOpen}
      isInputTypeFileSelected={isInputTypeFileSelected}
      isFontSizeCoefModalOpen={isFontSizeCoefModalOpen}
      isLaserOutputQualityModalOpen={isLaserOutputQualityModalOpen}
      isLoadCFWByArticleNumberModalOpen={isLoadCFWByArticleNumberModalOpen}
      handleSliderChange={handleSliderChange}
      handleCopyClick={handleCopyClick}
      handlePasteClick={handlePasteClick}
      handleAddPageClick={handleAddPageClick}
      handleUndoClick={handleUndoClick}
      handleRedoClick={handleRedoClick}
      handleOpenDeleteMenuClick={handleOpenDeleteMenuClick}
      handleCloseDeleteMenu={handleCloseDeleteMenu}
      handleOpenInfoMenuClick={handleOpenInfoMenuClick}
      handleCloseInfoMenu={handleCloseInfoMenu}
      handleOpenDotsMenuClick={handleOpenDotsMenuClick}
      handleCloseDotsMenu={handleCloseDotsMenu}
      handleArticleNumberModalOpen={handleArticleNumberModalOpen}
      handleSaveAsModalOpen={handleSaveAsModalOpen}
      handleLanguageModalOpen={handleLanguageModalOpen}
      handleSetFilteredLanguage={handleSetFilteredLanguage}
      handleLaserOutputClick={handleLaserOutputClick}
      handleLaserOutputContextMenuClose={handleLaserOutputContextMenuClose}
      handleSetLaserOutputQuality={handleSetLaserOutputQuality}
      handlePrinterModalOpen={handlePrinterModalOpen}
      handleInputTypeFileSelection={handleInputTypeFileSelection}
      handleFontSizeCoefModalOpen={handleFontSizeCoefModalOpen}
      handleLaserOutputQualityModalOpen={handleLaserOutputQualityModalOpen}
      handleLoadCFWByArticleNumberModalOpen={handleLoadCFWByArticleNumberModalOpen}
    />
  );
};

const mapStateToProps = state => {
  const { 
    pages,
    pagesSelection,
    sectionsSelection,
    shieldsSelection,
    copiedShield,
    copiedSection,
    copiedSections,
    snapshotPointer,
    snapshots,
    targetDevice,
    scaleCoef,
    laserOutputQuality,
    sectionsDisplayState,
    textTypingResource,
  } = state.core;

  const selectedSection = getSelectedSection({ sectionsSelection, pages });
  const selectedShield = getSelectedShield({ pages, shieldsSelection, selectedSection });
  const currentPage = pages[pagesSelection[0]] || null;
  const sections = currentPage?.sections || null;
  const layouts = currentPage?.layouts || {};
  const isMatrix = getMatrix({ pages, selectedShield, selectedSection, sections, layouts });
  const selectedSectionKeys = Object.keys(sectionsSelection);
  const sectionsSelectionCount = Object.values(sectionsSelection).length || 0;
  const hasPages = Object.keys(pages).length > 0;
  const matfileTemplate = getBuildedMatfileTemplate();
  const isMatfileTemplateAvailable = !!matfileTemplate;
  const isSeparatorExists = findSeparator({ sectionsSelection, pages });
  const disableDeleteElements = isSeparatorExists && (sectionsSelectionCount > 1 || selectedSection?.type !== SEPARATOR);

  const isDesktop = targetDevice === DESKTOP;
  const isUndoAvailable = snapshotPointer >= 0;
  const isRedoAvailable = snapshots.length > snapshotPointer + 1;
  const isCopyingAvailable = getIsCopyingAvailable({
    pages,
    sectionsSelection,
    isMatrix,
    textTypingResource
  });
  const isPastingAvailable = getIsPastingAvailable({
    copiedShield,
    copiedSection,
    copiedSections,
    selectedShield,
    textTypingResource
  });
  const isAddPageAvailable = hasPages;
  const isDeleteAvailable = !!currentPage;
  const isInfoAvailable = isMatfileTemplateAvailable;

  const isBackButtonShown = false;
  const materialWithLaserParameters = getMaterialWithLaserParameters();
  const isMaterialWithLaserParameters = !!hasPages && materialWithLaserParameters.available;
  const isLaserOutputQualitySelected = materialWithLaserParameters.$2 ? !!laserOutputQuality : true;
  const isDebugMenuVisible = getConfig().debug;

  return {
    isMatrix,
    isDesktop,
    scaleCoef,
    isUndoAvailable,
    isRedoAvailable,
    isCopyingAvailable,
    isPastingAvailable,
    isAddPageAvailable,
    isDeleteAvailable,
    isInfoAvailable,
    isBackButtonShown,
    isMaterialWithLaserParameters,
    materialWithLaserParameters,
    disableDeleteElements,
    laserOutputQuality,
    sectionsDisplayState,
    isLaserOutputQualitySelected,
    isDebugMenuVisible,
  };
};

export default connect(mapStateToProps, {
  handleZoomSliderChange,
  copySectionContent,
  pasteSectionContent,
  addPage,
})(HeaderContainer);
